/**
 * Telling the extension that a pin's ticket stopped being a pin (TOADO-405).
 *
 * The problem this solves: the extension has no live connection. It cannot -
 * an MV3 service worker is evicted when idle, so a WebSocket there means
 * fighting the platform over reconnect-on-wake, and it would mean every page
 * carrying the panel holding an open company-wide event stream. So the pin list
 * is fetched and cached for fifteen minutes, and a ticket archived in Toado
 * leaves its pin drawn on the page for up to that long.
 *
 * This tab already has the live subscription and already hears about it. So it
 * tells the extension, over the `postMessage` bridge `content/captureHooks.ts`
 * already runs for `toado:closeTab` and `toado:capture`.
 *
 * Only works while a Toado tab is open, which is the reported case: somebody
 * archives a ticket and turns straight back to the page. TOADO-406 is the floor
 * under everything else.
 */
import type { LiveEvent } from '@vd/types';

/**
 * The one message type, carrying one field.
 *
 * Deliberately NOT the new pin set, and deliberately not even which lifecycle
 * event happened. Both would be a page telling the extension what to draw, and
 * this bridge accepts messages from a web page. The extension's response is to
 * drop what it cached and ask the API again, so the only thing this message has
 * to be is a hint that the answer changed. A hostile page that sent this gets a
 * refetch, which is the same thing switching tabs gets.
 *
 * Not carrying the event kind also removes the branching: archived, deleted,
 * restored and moved all mean "ask again", and a refetch is right for all four
 * without this side having to reason about which.
 */
export const PIN_RELAY_MESSAGE = 'toado:ticket-changed';

export interface PinRelayMessage {
  type: typeof PIN_RELAY_MESSAGE;
  ticketId: string;
}

/**
 * The lifecycle events that change whether a ticket is a pin on a page.
 *
 * `ticket.moved` is in the set because a move can cross projects, and a ticket
 * that lands in a project the panel is not showing should stop being drawn.
 * That is the same family as the orphaned column id in CLAUDE.md's mistakes
 * log: a cross-project move whose consequences were not followed through.
 *
 * `ticket.updated` is NOT in the set. It fires on every autosaved keystroke's
 * debounce and on every AI title arrival, and none of those change whether a
 * pin belongs on a page. Including it would turn this into a refetch storm.
 */
const RELAYED: ReadonlySet<LiveEvent['type']> = new Set([
  'ticket.archived',
  'ticket.restored',
  'ticket.deleted',
  'ticket.moved',
]);

export function isPinRelayEvent(event: LiveEvent): boolean {
  return RELAYED.has(event.type);
}

/**
 * Post the hint, if this event is one of the four.
 *
 * Targeted at this page's own origin rather than `'*'`. The listener checks the
 * same thing from its side, but a message addressed to one origin cannot be
 * read by a frame from another, and there is no reason to broadcast wider than
 * the one listener that wants it.
 *
 * Silent when the extension is not installed, which is the common case: the
 * message goes out, nothing is listening, nothing happens.
 */
export function relayTicketChangeToExtension(
  event: LiveEvent,
  target: Pick<Window, 'postMessage' | 'location'> = window,
): void {
  if (!isPinRelayEvent(event)) return;
  const ticketId = (event as { ticketId?: unknown }).ticketId;
  if (typeof ticketId !== 'string' || !ticketId) return;
  const message: PinRelayMessage = { type: PIN_RELAY_MESSAGE, ticketId };
  try {
    target.postMessage(message, target.location.origin);
  } catch {
    // A sandboxed document can refuse postMessage. Nothing here is worth
    // failing a live-event handler over - the worst case is the pin staying
    // until TOADO-406's refetch picks it up.
  }
}

/**
 * Telling the extension the workspace changed (TOADO-761, TOADO-832): a
 * person's name, the members, a ticket type, a project or the sites it
 * covers. The extension keeps the people a pin can be assigned to and the
 * projects that claim each site; on this hint it drops the people and reads
 * the projects again, so a rename or a new URL pattern shows on the next pin
 * without a reload. As with the ticket hint, it carries nothing to act on
 * but "ask again", so a page that sends it gets a refetch.
 *
 * A switch of workspace also names the one switched to (t-LnUUVu), so the
 * extension works in the same workspace. That is the one value the hint
 * carries, and the extension takes it only from this app's own tab and only
 * when it is a workspace the account belongs to.
 */
export const WORKSPACE_RELAY_MESSAGE = 'toado:workspace-changed';

/** The writes that change what the extension keeps, by path; any method but a read. */
const WORKSPACE_WRITES: readonly RegExp[] = [
  /^\/me(?:\/avatar)?$/,
  /^\/companies(?:\/[^/]+)?$/,
  /^\/companies\/[^/]+\/(?:members|projects|ticket-types)(?:\/.*)?$/,
  /^\/projects\/[^/]+(?:\/widget|\/ticket-types(?:\/.*)?)?$/,
  /^\/invites\/[^/]+$/,
  /^\/auth\/accept-invite$/,
];

export function isWorkspaceWrite(method: string | undefined, path: string): boolean {
  const verb = (method ?? 'GET').toUpperCase();
  if (verb === 'GET' || verb === 'HEAD') return false;
  const bare = path.split(/[?#]/, 1)[0]!;
  return WORKSPACE_WRITES.some((pattern) => pattern.test(bare));
}

/** Post the hint after a write that succeeded, or a switch; silent when no extension listens. */
export function tellExtensionWorkspaceChanged(
  target: Pick<Window, 'postMessage' | 'location'> = window,
  workspaceId?: string,
): void {
  try {
    target.postMessage({ type: WORKSPACE_RELAY_MESSAGE, ...(workspaceId ? { workspaceId } : {}) }, target.location.origin);
  } catch {
    // A sandboxed document can refuse postMessage; the extension asks again
    // on its own within minutes.
  }
}
